import { useMemo } from "react";
import type { EFFeatureDefinition } from "@/features/runtime/FeatureDefinition";
import {
	getRequiredFeatureDefinition,
	resolveFeatureIdForDockTarget,
} from "@/features/runtime/FeatureRegistry";
import type { HostSessionState } from "@/hooks/useHostSession";
import type { ProviderRuntimeHookState } from "@/hooks/useProviderRuntime";
import type { UseShellRuntimeState } from "@/shell/useShellRuntime";
import type {
	EFFeatureDockTarget,
	EFFeatureId,
	EFFeatureRuntime,
	EFFeatureRuntimeView,
} from "@/features/runtime/FeatureTypes";
import type { UseFeatureEventsResult } from "@/features/runtime/useFeatureEvents";
import { debugLog } from "@/utils/debug";

export type UseFeatureRuntimeInput = {
	host: HostSessionState;
	providerRuntime: ProviderRuntimeHookState;
	shell: UseShellRuntimeState;
	featureEvents: UseFeatureEventsResult;
	featureRegistry: ReadonlyMap<EFFeatureId, EFFeatureDefinition>;
};

function toFeatureDockTarget(
	value: string | null | undefined
): EFFeatureDockTarget | null {
	if (value === "mission" || value === "tournament") {
		return value;
	}

	return null;
}

function resolveFeatureDockTargetOrThrow(
	featureRegistry: ReadonlyMap<EFFeatureId, EFFeatureDefinition>,
	featureId: EFFeatureId
): EFFeatureDockTarget {
	const definition = getRequiredFeatureDefinition(featureRegistry, featureId);

	if (!definition.defaultDockTarget) {
		throw new Error(
			`[FeatureRuntime] feature '${featureId}' cannot be activated because it does not declare defaultDockTarget`
		);
	}

	return definition.defaultDockTarget;
}

export function useFeatureRuntime({
	host,
	providerRuntime,
	shell,
	featureEvents,
	featureRegistry,
}: UseFeatureRuntimeInput): EFFeatureRuntime {
	return useMemo<EFFeatureRuntime>(() => {
		const activeDockTarget = toFeatureDockTarget(shell.activeDockTarget);
		const activeFeatureId = resolveFeatureIdForDockTarget(
			featureRegistry,
			activeDockTarget
		);
		const popupFeatureId = shell.popupVisible ? activeFeatureId : null;

		const runtimeView: EFFeatureRuntimeView = {
			host: {
				configured: host.hostConfigured,
				gameReady: host.hostGameReady,
				platform: host.providerBootInput?.platform ?? "desktop",
				orientation: host.providerBootInput?.orientation ?? "landscape",
				lang: host.providerBootInput?.lang ?? null,
				gameName: host.providerBootInput?.gameName ?? null,
				operator: host.providerBootInput?.operator ?? null,
			},
			provider: {
				available: !!providerRuntime.provider,
				ready: providerRuntime.providerReady,
				providerId:
					providerRuntime.providerRuntimeSnapshot.providerId ===
					"captainup"
						? "captainup"
						: null,
			},
			shell: {
				popupVisible: shell.popupVisible,
				activeDockTarget,
			},
			selection: {
				activeFeatureId,
				popupFeatureId,
			},
		};

		const runtime: EFFeatureRuntime = {
			view: runtimeView,
			actions: {
				activateFeature: (featureId: EFFeatureId) => {
					const target = resolveFeatureDockTargetOrThrow(
						featureRegistry,
						featureId
					);

					debugLog("[FeatureRuntime] activateFeature()", {
						featureId,
						target,
						currentDockTarget: shell.activeDockTarget,
						popupVisible: shell.popupVisible,
						popupFeatureId,
					});

					shell.setActiveDockTarget(target);
				},
				openFeaturePopup: (featureId: EFFeatureId) => {
					const target = resolveFeatureDockTargetOrThrow(
						featureRegistry,
						featureId
					);

					debugLog("[FeatureRuntime] openFeaturePopup()", {
						featureId,
						target,
						currentDockTarget: shell.activeDockTarget,
						popupVisible: shell.popupVisible,
						popupFeatureId,
					});

					shell.setActiveDockTarget(target);
					shell.requestPopupOpen();
				},
				closeFeaturePopup: () => {
					debugLog("[FeatureRuntime] closeFeaturePopup()", {
						currentDockTarget: shell.activeDockTarget,
						popupVisible: shell.popupVisible,
						popupFeatureId,
					});

					shell.handlePopupClose();
				},
			},
			events: {
				subscribeRewardAcquired: (handler) =>
					featureEvents.subscribeRewardAcquired(handler),
			},
		};

		return runtime;
	}, [featureEvents, featureRegistry, host, providerRuntime, shell]);
}

export default useFeatureRuntime;